import {
  Badge,
  Container,
  EmptyState,
  Flex,
  Heading,
  IconButton,
  Table,
  Text,
  VStack,
  Link,
} from "@chakra-ui/react"
import { FiEdit2 } from 'react-icons/fi';
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate, Link as RouterLink } from "@tanstack/react-router"
import { FiSearch } from "react-icons/fi"
import { z } from "zod"

import { ProviderUserGroups, ProviderUsers as ProviderUsersApi, ProviderGroups, ProviderUserTypes } from "@/client"
import AddProviderUser from "@/components/ProviderUsers/AddProviderUser"
import ManageGroupMembershipsDialog from "@/components/ProviderUsers/ManageGroupMembershipsDialog"
import PendingProviderUsers from "@/components/ProviderUsers/PendingProviderUsers"
import useAuth from "@/hooks/useAuth"
import { UserRole } from "@/types/permissions"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination.tsx"

const ProviderUsersSearchSchema = z.object({
  page: z.number().catch(1),
})

const PER_PAGE = 5

function getProviderUsersQueryOptions({ page, providerId }: { page: number; providerId: string }) {
  return {
    queryFn: () =>
      ProviderUsersApi.readProviderUsers({ query: { providerId, skip: (page - 1) * PER_PAGE, limit: PER_PAGE } }),
    queryKey: ["providerUsers", { page, providerId }],
  }
}

function getProviderGroupsQueryOptions({ providerId }: { providerId: string }) {
  return {
    queryFn: () =>
      ProviderGroups.readProviderGroupsByProviderId({ path: { providerId } }),
    queryKey: ["providerGroups", { providerId }],
  }
}

export const Route = createFileRoute("/_layout/provider/providerusers/$providerId")({
  component: ProviderUsers,
  validateSearch: (search) => ProviderUsersSearchSchema.parse(search),
})

function ProviderUsersTable({ isProviderAdmin }: { isProviderAdmin?: boolean }) {
  const params = Route.useParams()
  const providerId = params.providerId
  const { page } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getProviderUsersQueryOptions({ page, providerId }),
    placeholderData: (prevData) => prevData,
  })

  const { data: userTypesData } = useQuery({
    queryKey: ["providerUserTypes", providerId],
    queryFn: () => ProviderUserTypes.listProviderUserTypes({ query: { providerId } }),
    enabled: !!providerId,
  })
  const userTypeMap = new Map((userTypesData?.data ?? []).map((t) => [t.id, t.name]))

  // Query to check for provider groups
  const { data: providerGroupsData, isLoading: providerGroupsLoading } = useQuery({
    ...getProviderGroupsQueryOptions({ providerId }),
    enabled: !!providerId,
  })

  const setPage = (page: number) =>
    navigate({
      search: (prev: { page: number }) => ({ ...prev, page }),
    })

  const ProviderUsers = data?.data.slice(0, PER_PAGE) ?? []
  const count = data?.count ?? 0
  const providerGroups = providerGroupsData?.data ?? []

  if (isLoading) {
    return <PendingProviderUsers />
  }

  if (ProviderUsers.length === 0) {
    // Check if there are no provider groups
    if (!providerGroupsLoading && providerGroups.length === 0) {
      return (
        <EmptyState.Root>
          <EmptyState.Content>
            <EmptyState.Indicator>
              <FiSearch />
            </EmptyState.Indicator>
            <VStack textAlign="center">
              <EmptyState.Title>Provider group required</EmptyState.Title>
              <EmptyState.Description>
                You need to create a provider group before adding provider users.{" "}
                <Link 
                  href={`/providergroups/${providerId}`}
                  color="blue.500" 
                  textDecoration="underline"
                >
                  Go to Provider Groups
                </Link>
              </EmptyState.Description>
            </VStack>
          </EmptyState.Content>
        </EmptyState.Root>
      )
    }

    // Normal empty state when provider groups exist but no users
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <FiSearch />
          </EmptyState.Indicator>
          <VStack textAlign="center">
            <EmptyState.Title>You don't have any provider users yet</EmptyState.Title>
            <EmptyState.Description>
              Add a new provider user to get started
            </EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    )
  }

  return (
    <>
      <Table.Root size="sm" variant="outline">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Name</Table.ColumnHeader>
            <Table.ColumnHeader>Email</Table.ColumnHeader>
            <Table.ColumnHeader>Groups</Table.ColumnHeader>
            <Table.ColumnHeader>User Type</Table.ColumnHeader>
            <Table.ColumnHeader>Role</Table.ColumnHeader>
            {isProviderAdmin && <Table.ColumnHeader w="60px" />}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {ProviderUsers?.map((ProviderUser: any) => (
            <Table.Row key={ProviderUser.id} opacity={isPlaceholderData ? 0.5 : 1}>
              <Table.Cell fontWeight="medium">
                {ProviderUser.displayName || <span style={{ color: "#A0AEC0" }}>—</span>}
                {ProviderUser.fullNameOverride && ProviderUser.full_name && (
                  <Text fontSize="xs" color="gray.400" mt={0.5}>{ProviderUser.full_name}</Text>
                )}
              </Table.Cell>
              <Table.Cell color={!ProviderUser.email ? "gray.400" : "inherit"}>
                {ProviderUser.email || "—"}
              </Table.Cell>
              <Table.Cell>
                <Flex gap={1} flexWrap="wrap">
                  {(ProviderUser.providerGroups ?? []).length === 0 ? (
                    <span style={{ color: "#A0AEC0" }}>—</span>
                  ) : (
                    (ProviderUser.providerGroups ?? []).map((g: any) => {
                      const isAdmin = (ProviderUser.adminGroupIds ?? []).includes(g.id ?? "")
                      return (
                        <Badge
                          key={g.id}
                          colorPalette={isAdmin ? "purple" : "gray"}
                          variant={isAdmin ? "subtle" : "outline"}
                          size="sm"
                        >
                          {g.name}{isAdmin ? " ★" : ""}
                        </Badge>
                      )
                    })
                  )}
                </Flex>
              </Table.Cell>
              <Table.Cell>
                {(ProviderUser.providerUserTypeIds ?? []).filter((id: any) => userTypeMap.has(id)).length > 0 ? (
                  <Flex gap={1} flexWrap="wrap">
                    {(ProviderUser.providerUserTypeIds ?? []).filter((id: any) => userTypeMap.has(id)).map((id: any) => (
                      <Badge key={id} colorPalette="blue" variant="subtle" size="sm">
                        {userTypeMap.get(id)}
                      </Badge>
                    ))}
                  </Flex>
                ) : (
                  <span style={{ color: "#A0AEC0" }}>—</span>
                )}
              </Table.Cell>
              <Table.Cell>
                {ProviderUser.isProviderAdmin ? (
                  <Badge colorPalette="purple" size="sm">Admin</Badge>
                ) : (
                  <span style={{ color: "#A0AEC0" }}>—</span>
                )}
              </Table.Cell>
              {isProviderAdmin && (
                <Table.Cell>
                  <RouterLink to="/provider/providerusers/edit" search={{ providerUserId: ProviderUser.id }}>
                    <IconButton aria-label="Edit user" size="xs" variant="ghost">
                      <FiEdit2 />
                    </IconButton>
                  </RouterLink>
                </Table.Cell>
              )}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
      <Flex justifyContent="flex-end" mt={4}>
        <PaginationRoot
          count={count}
          pageSize={PER_PAGE}
          onPageChange={({ page }) => setPage(page)}
        >
          <Flex>
            <PaginationPrevTrigger />
            <PaginationItems />
            <PaginationNextTrigger />
          </Flex>
        </PaginationRoot>
      </Flex>
    </>
  )
}

function ProviderUsers() {
  const params = Route.useParams()
  const providerId = params.providerId
  const { checkRole, user } = useAuth()
  const isProviderAdmin = checkRole(UserRole.PROVIDER_ADMIN)
  const isGroupAdminOnly = !isProviderAdmin && checkRole(UserRole.PROVIDER_GROUP_ADMIN)

  const { data: providerGroupsData, isLoading: providerGroupsLoading } = useQuery({
    ...getProviderGroupsQueryOptions({ providerId }),
    enabled: !!providerId,
  })

  // Fetch the current user's group memberships to find which groups they admin
  const { data: myMembershipsData } = useQuery({
    queryKey: ["myGroupMemberships", providerId, user?.id],
    queryFn: () =>
      ProviderUserGroups.readProviderUserGroupMembership({ query: { providerId } }),
    enabled: !!providerId && isGroupAdminOnly,
  })
  const adminGroupIds = (myMembershipsData ?? [])
    .filter((m) => m.isProviderGroupAdmin)
    .map((m) => m.providerGroupId)

  const providerGroups = providerGroupsData?.data ?? []
  const hasProviderGroups = !providerGroupsLoading && providerGroups.length > 0

  return (
    <Container maxW="full">
      <Heading size="lg" pt={12}>
        Provider Users Management
      </Heading>
      <Flex gap={2} mt={2} mb={2} flexWrap="wrap">
        {hasProviderGroups && isProviderAdmin && <AddProviderUser />}
        {isGroupAdminOnly && adminGroupIds.length > 0 && (
          <ManageGroupMembershipsDialog providerId={providerId} adminGroupIds={adminGroupIds} />
        )}
      </Flex>
      <ProviderUsersTable isProviderAdmin={isProviderAdmin} />
    </Container>
  )
}